import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { REGISTER } from "@/constants/testIds";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await register(name, email, password);
      toast.success("Welcome to Agrovest! Now complete your contestant application.");
      navigate("/apply");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid md:grid-cols-2">
      <div className="hidden md:flex relative bg-black p-12 items-end overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/0m8kloux_514355381_754040930458457_7600060261891631209_n.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
        <div className="relative z-10">
          <Logo className="h-12 w-auto mb-8" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-3">
            Season 1 · Show Me The Money
          </div>
          <h2
            className="text-4xl md:text-5xl font-black tracking-tighter leading-[1]"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Your shot at <span className="text-[#F26A1F]">₦20 Million</span> starts here.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-5">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F]">Create your account</div>
          <h1
            className="text-4xl font-black tracking-tighter leading-[1]"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Register for Agrovest
          </h1>
          <p className="text-white/60 text-sm">
            Free and open to Nigerian citizens aged 18–35. Already have an account?{" "}
            <Link to="/login" className="text-[#F26A1F] font-bold underline">Login</Link>.
          </p>

          <Field label="Full Name" value={name} onChange={setName} testid={REGISTER.nameInput} required />
          <Field label="Email" type="email" value={email} onChange={setEmail} testid={REGISTER.emailInput} required />
          <Field label="Password" type="password" value={password} onChange={setPassword} testid={REGISTER.passwordInput} required />
          <Field label="Confirm Password" type="password" value={confirm} onChange={setConfirm} testid={REGISTER.passwordConfirmInput} required />

          <button
            type="submit"
            data-testid={REGISTER.submitButton}
            disabled={submitting}
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors"
          >
            {submitting ? "Creating account..." : "Create Account"}
          </button>

          <div className="text-center text-xs text-white/40 pt-4">
            Looking to formally apply as a contestant?{" "}
            <Link to="/apply" className="text-[#2BA84A] underline">Submit the application form</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full h-12 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
      />
    </div>
  );
}
